import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { API } from "../api/api";
import { END_POINTS } from "../api/endpoints";
import usePermissions from "../utils/permissions";
import { getStableIdempotencyKey } from "../utils/billing";
import { useHospitalContext } from "../context/HospitalContext";

const OFFLINE_METHOD_LABELS = {
  CASH: "Cash",
  DIRECT_UPI: "Direct UPI",
  CARD_TERMINAL: "Card terminal",
};

const PAGE_SIZE = 50;

const formatAppointment = (appointmentDate) => {
  const date = moment(appointmentDate);
  return date.isValid() ? date.format("DD MMM YYYY · hh:mm A") : "Date unavailable";
};

const paymentStateLabel = {
  UNPAID: "Unpaid",
  PENDING_VERIFICATION: "Online payment pending",
  PAID_ONLINE: "Paid online",
  PAID_OFFLINE: "Paid offline",
  PAYMENT_NOT_CONFIRMED: "Payment not confirmed",
  REVIEW_REQUIRED: "Billing review required",
};

const formatAmount = (amountMinor, currency) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return `${currency} ${(amountMinor / 100).toFixed(2)}`;
  }
};

const BillingQueue = () => {
  const { selectedHospital } = useHospitalContext();
  const { hasPermission } = usePermissions();
  const [items, setItems] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [offlineMethod, setOfflineMethod] = useState("");
  const [recording, setRecording] = useState(false);
  const offlineKeyRef = useRef(null);
  const [view, setView] = useState("outstanding");

  const canView = hasPermission("BILL_VIEW");
  const canRecordOffline = hasPermission("OFFLINE_PAYMENT_CREATE");

  const loadQueue = useCallback(async (isCurrent = () => true) => {
    if (!selectedHospital?.id || !canView) {
      setItems([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const response = await API.get(
        END_POINTS.billingCollectionQueue(search, page, PAGE_SIZE, view),
      );
      // Ignore responses for a previous tab/page/search.
      if (!isCurrent()) return;
      if (response?.success) {
        setItems(Array.isArray(response.data) ? response.data : []);
        setTotal(response.meta?.total ?? 0);
        setError("");
      } else {
        setItems([]);
        setTotal(0);
        setError(response?.message || "Unable to load billing collection queue");
      }
    } catch (requestError) {
      if (!isCurrent()) return;
      setItems([]);
      setTotal(0);
      setError(requestError?.message || "Unable to load billing collection queue");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [canView, page, search, selectedHospital?.id, view]);

  useEffect(() => {
    setPage(1);
  }, [selectedHospital?.id]);

  useEffect(() => {
    let active = true;
    const poll = () => loadQueue(() => active);
    poll();
    const intervalId = setInterval(poll, 10000);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [loadQueue]);

  const openOfflineDialog = (bill) => {
    setSuccess("");
    setSelectedBill(bill);
    const methods = (bill.offlineMethods || []).filter(
      (method) => OFFLINE_METHOD_LABELS[method],
    );
    setOfflineMethod(methods[0] || "");
  };

  const closeOfflineDialog = () => {
    if (recording) return;
    setSelectedBill(null);
    setOfflineMethod("");
  };

  const recordOfflinePayment = async () => {
    if (!selectedBill || !offlineMethod || recording) return;

    setRecording(true);
    setSuccess("");
    try {
      const response = await API.post(
        END_POINTS.recordOfflinePayment(selectedBill.billId),
        {
          method: offlineMethod,
          amountMinor: selectedBill.amountMinor,
          idempotencyKey: getStableIdempotencyKey(
            offlineKeyRef,
            `${selectedBill.billId}:${offlineMethod}`,
          ),
        },
      );

      if (!response?.success) {
        setError(response?.message || "Unable to record offline payment");
        return;
      }

      setError("");
      offlineKeyRef.current = null;
      setSuccess("Offline payment recorded successfully.");
      setSelectedBill(null);
      setOfflineMethod("");
      await loadQueue();
    } catch (requestError) {
      setError(requestError?.message || "Unable to record offline payment");
    } finally {
      setRecording(false);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to view billing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "80vh" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        mb={1}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Billing collection
          </Typography>
          <Typography color="text.secondary">
            {view === "paid"
              ? "Completed ONI-billing consultation payments for this hospital."
              : "Outstanding ONI-billing consultation payments for this hospital."}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadQueue} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Online payment is completed by the patient in Oni-Mom. Use offline
        confirmation only after the full amount has been received.
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs
        value={view}
        onChange={(_, nextView) => {
          setView(nextView);
          setPage(1);
        }}
        sx={{ mb: 2 }}
      >
        <Tab value="outstanding" label="Outstanding" />
        <Tab value="paid" label="Paid history" />
      </Tabs>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={2}>
        <TextField
          fullWidth
          size="small"
          label="Search patient, doctor, or appointment"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setPage(1);
              setSearch(searchInput.trim());
            }
          }}
        />
        <Button
          variant="contained"
          onClick={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
          sx={{ minWidth: { sm: 110 } }}
        >
          Search
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!selectedHospital?.id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a hospital to view its collection queue.
        </Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Appointment</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell align="right">
                  {view === "paid" ? "Amount" : "Amount due"}
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" py={3}>
                      {view === "paid"
                        ? "No paid consultation payments yet."
                        : "No outstanding consultation payments."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.billId}>
                    <TableCell>
                      <Typography fontWeight={600}>{item.patientName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.patientId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{item.doctorName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatAppointment(item.appointmentDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.paymentType === "PREPAID" ? "Pay before" : "Pay later"}
                        color={item.paymentType === "PREPAID" ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(item.amountMinor, item.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={paymentStateLabel[item.paymentState] || item.paymentState}
                        color={item.paymentState === "PENDING_VERIFICATION" ? "warning" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {view === "paid" ? (
                        <Typography variant="caption" color="text.secondary">
                          Completed
                        </Typography>
                      ) : item.canConfirmOffline && canRecordOffline ? (
                        <Button size="small" onClick={() => openOfflineDialog(item)}>
                          Confirm offline
                        </Button>
                      ) : item.paymentState === "REVIEW_REQUIRED" ? (
                        <Typography variant="caption" color="error">
                          Review billing
                        </Typography>
                      ) : item.paymentState === "PENDING_VERIFICATION" ||
                        ["CREATED", "PENDING"].includes(item.onlinePaymentStatus) ? (
                        <Typography variant="caption" color="text.secondary">
                          Waiting for Oni-Mom
                        </Typography>
                      ) : item.paymentState === "PAYMENT_NOT_CONFIRMED" && item.canPayOnline ? (
                        <Typography variant="caption" color="primary">
                          Patient can retry in Oni-Mom
                        </Typography>
                      ) : item.canPayOnline ? (
                        <Typography variant="caption" color="primary">
                          Patient can pay in Oni-Mom
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Review payment setup
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {total} {view === "paid" ? "paid bill" : "outstanding bill"}
              {total === 1 ? "" : "s"}
            </Typography>
            {total > PAGE_SIZE && (
              <Pagination
                size="small"
                count={Math.ceil(total / PAGE_SIZE)}
                page={page}
                onChange={(_, nextPage) => setPage(nextPage)}
                color="primary"
              />
            )}
          </Stack>
        </Box>
      </Paper>

      <Dialog open={Boolean(selectedBill)} onClose={closeOfflineDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm offline collection</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Confirm only after receiving the full {formatAmount(selectedBill?.amountMinor || 0, selectedBill?.currency || "INR")}.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(selectedBill?.offlineMethods || [])
              .filter((method) => OFFLINE_METHOD_LABELS[method])
              .map((method) => (
              <Button
                key={method}
                variant={offlineMethod === method ? "contained" : "outlined"}
                onClick={() => setOfflineMethod(method)}
              >
                {OFFLINE_METHOD_LABELS[method] || method}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOfflineDialog} disabled={recording}>Cancel</Button>
          <Button
            variant="contained"
            onClick={recordOfflinePayment}
            disabled={!offlineMethod || recording}
          >
            {recording ? "Saving…" : "Confirm funds collected"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingQueue;
